import { GridsterItem } from 'angular-gridster2';
import { KpiRangeSetting } from '../kpi-range-fns';

/**
 * Configuration for a dashboard widget's position and size in the grid.
 * Extends GridsterItem with our custom properties.
 */
export interface DashboardWidgetConfig extends GridsterItem {
  /** Unique identifier for this widget instance */
  id: string;
  /** The type of widget (used to instantiate the correct component) */
  widgetType: WidgetType;
}

/**
 * Available widget types for the dashboard.
 * Add new widget types here as they are created.
 *
 * NOTE: This is adapted from Proximity's dashboard.
 * Uniformax-specific widget types may differ.
 */
export type WidgetType =
  | 'jo-in-process' // Count of job orders in process
  | 'jo-stats' // Job order statistics (existing chart)
  | 'invoice-stats' // Invoice statistics (existing chart)
  | 'invoice-delay' // Invoice delay metrics (existing chart)
  | 'inventory-count' // Manufacturer inventory count (existing chart)
  | 'product-type-inventory' // Top 10 product types by inventory for selected manufacturer
  | 'account-fulfillment-time' // Avg order-to-ship days by account, top 10 + balance
  | 'manufacturer-receipt-time' // Avg P/O-placed-to-received days by manufacturer, actual vs promised
  | 'account-overdue-orders' // Accounts ranked by job orders past their promised ship date
  | 'weekly-shipment-revenue' // Weekly shipped revenue (bars) against shipment count (line)
  | 'red-zone' // Open job order details aged against the original promised ship date
  | 'on-time-shipped-count' // KPI: units shipped by the original promised ship date, and their share
  | 'on-time-shipped-value' // KPI: billed value shipped by the original promised ship date, and its share
  | 'orders-shipped-complete' // KPI: orders first shipped in the range that shipped complete in that first shipment
  | 'process-station-units'; // KPI: units that finished add-on work in the range, per Process Station

/**
 * Metadata about a widget type (for the widget picker UI)
 */
export interface WidgetTypeInfo {
  type: WidgetType;
  name: string;
  description: string;
  defaultCols: number;
  defaultRows: number;
  minCols?: number;
  minRows?: number;
  maxCols?: number;
  maxRows?: number;
  /** Whether the widget supports viewing data in a grid dialog and Excel export */
  supportsDataView?: boolean;
  /** A small one- or two-number tile driven by the dashboard's KPI Range. No data view, no export. */
  isKpi?: boolean;
  /** Reads the dashboard's Account Filter; shows a FILTER badge on the tile and the Add Widget card. */
  usesAccountFilter?: boolean;
}

/**
 * Registry of available widget types with their metadata.
 * These match the existing chart components in Uniformax.
 */
export const WIDGET_TYPE_REGISTRY: WidgetTypeInfo[] = [
  {
    type: 'jo-in-process',
    usesAccountFilter: true,
    name: 'JO In Process',
    description: 'Count of job orders currently in process',
    defaultCols: 2,
    defaultRows: 2,
    minCols: 2,
    minRows: 2,
    supportsDataView: true,
  },
  {
    type: 'jo-stats',
    name: 'Job Order Statistics',
    description: 'Chart showing current job orders by age and status',
    defaultCols: 4,
    defaultRows: 3,
    minCols: 3,
    minRows: 2,
    supportsDataView: true,
  },
  {
    type: 'invoice-stats',
    usesAccountFilter: true,
    name: 'Invoice Statistics',
    description: 'Chart showing invoice trends over time',
    defaultCols: 4,
    defaultRows: 3,
    minCols: 3,
    minRows: 2,
    supportsDataView: true,
  },
  {
    type: 'invoice-delay',
    usesAccountFilter: true,
    name: 'Invoice Delay',
    description: 'Chart showing invoice delay metrics',
    defaultCols: 4,
    defaultRows: 3,
    minCols: 3,
    minRows: 2,
    supportsDataView: true,
  },
  {
    type: 'inventory-count',
    name: 'Manufacturer Inventory',
    description: 'Chart showing inventory counts by manufacturer',
    defaultCols: 4,
    defaultRows: 3,
    minCols: 3,
    minRows: 2,
    supportsDataView: true,
  },
  {
    type: 'product-type-inventory',
    name: 'Product Type Inventory',
    description: 'Top 10 product types by inventory count for selected manufacturer',
    defaultCols: 5,
    defaultRows: 4,
    minCols: 4,
    minRows: 3,
    supportsDataView: true,
  },
  {
    type: 'account-fulfillment-time',
    name: 'Average Time by Account',
    description: 'Shipped items per account over the last 6 months, with the average order-to-ship days',
    defaultCols: 5,
    defaultRows: 4,
    minCols: 4,
    minRows: 3,
    supportsDataView: true,
  },
  {
    type: 'manufacturer-receipt-time',
    name: 'Average Receipt Time by Manufacturer',
    description: 'Days from the purchase order going out to the units being received, actual against promised',
    defaultCols: 6,
    defaultRows: 4,
    minCols: 4,
    minRows: 3,
    supportsDataView: true,
  },
  {
    type: 'account-overdue-orders',
    name: 'Overdue Units by Account',
    description: 'Accounts ranked by open job order units that are past the ship date promised to the customer',
    defaultCols: 5,
    defaultRows: 4,
    minCols: 4,
    minRows: 3,
    supportsDataView: true,
  },
  {
    type: 'weekly-shipment-revenue',
    name: 'Weekly Shipment Revenue and Volume',
    description: 'Revenue billed on the shipments that left each week, against how many shipments left',
    defaultCols: 6,
    defaultRows: 5,
    minCols: 4,
    minRows: 4,
    supportsDataView: true,
  },
  {
    type: 'red-zone',
    usesAccountFilter: true,
    name: 'Red Zone',
    description: 'Open job order details by days past the original promised ship date. Uses the Account Filter, or all accounts',
    defaultCols: 5,
    defaultRows: 4,
    minCols: 4,
    minRows: 3,
    supportsDataView: true,
  },
  {
    type: 'on-time-shipped-count',
    name: 'On-Time Shipped Count',
    description:
      'KPI: units shipped on or before the original promised ship date, and their percentage of all units shipped. Uses the KPI Range. Always every account',
    defaultCols: 2,
    defaultRows: 2,
    minCols: 2,
    minRows: 2,
    supportsDataView: false,
    isKpi: true,
  },
  {
    type: 'on-time-shipped-value',
    name: 'On-Time Shipped Value',
    description:
      'KPI: billed value of the units shipped on or before the original promised ship date, and its percentage of all value shipped. Uses the KPI Range. Always every account',
    defaultCols: 2,
    defaultRows: 2,
    minCols: 2,
    minRows: 2,
    supportsDataView: false,
    isKpi: true,
  },
  {
    type: 'orders-shipped-complete',
    name: 'Orders Shipped Complete',
    description:
      'KPI: job orders shipped for the first time that shipped complete in that one shipment, and their percentage of all job orders shipped for the first time. Uses the KPI Range. Always every account',
    defaultCols: 2,
    defaultRows: 2,
    minCols: 2,
    minRows: 2,
    supportsDataView: false,
    isKpi: true,
  },
  {
    type: 'process-station-units',
    name: 'Process Station Units',
    description:
      'KPI: units that completed each Process Station. A unit that goes through two stations counts at each station. Uses the KPI Range. Always every account',
    defaultCols: 2,
    defaultRows: 2,
    minCols: 2,
    minRows: 2,
    supportsDataView: false,
    isKpi: true,
  },
];

/**
 * Complete dashboard configuration that gets persisted
 */
export interface DashboardConfig {
  widgets: DashboardWidgetConfig[];
  version: number; // For future migration support
  /** The KPI Range shared by every KPI widget. Missing on layouts saved before KPIs existed. */
  kpiRange?: KpiRangeSetting;
}

/**
 * Creates a default dashboard configuration for new users.
 * Uses the existing Uniformax chart components.
 */
export function createDefaultDashboardConfig(): DashboardConfig {
  return {
    version: 1,
    widgets: [
      { id: 'widget-1', widgetType: 'jo-in-process', x: 0, y: 0, cols: 2, rows: 2 },
      { id: 'widget-2', widgetType: 'jo-stats', x: 2, y: 0, cols: 5, rows: 3 },
      { id: 'widget-3', widgetType: 'invoice-stats', x: 7, y: 0, cols: 5, rows: 3 },
      { id: 'widget-4', widgetType: 'invoice-delay', x: 0, y: 3, cols: 6, rows: 3 },
      { id: 'widget-5', widgetType: 'inventory-count', x: 6, y: 3, cols: 6, rows: 3 },
    ],
  };
}
